/**
 * Playground interceptor handlers for User and Invitation management.
 * Matches API patterns: /v1/web/user, /v1/web/invitation
 */
import { PlaygroundSessionStore, PlaygroundLimitError } from '../PlaygroundSessionStore.js';

const invitationStore = new PlaygroundSessionStore('pg_invitations', 5);
const now = () => new Date().toISOString();

/**
 * Read the playground users list from sessionStorage.
 */
function getUsers() {
    try {
        const raw = sessionStorage.getItem('pg_users');
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

/**
 * Read the playground user object from sessionStorage.
 */
function getPlaygroundUser() {
    try {
        const raw = sessionStorage.getItem('pg_org');
        const org = raw ? JSON.parse(raw) : {};
        return {
            userId: 'pg-user-001',
            userName: 'Playground Explorer',
            email: 'explorer@playground.geoplay.io',
            orgId: org.id || 'pg-org-001',
            roleId: 'role-admin',
            roleName: 'Admin',
        };
    } catch {
        return {
            userId: 'pg-user-001',
            userName: 'Playground Explorer',
            email: 'explorer@playground.geoplay.io',
            orgId: 'pg-org-001',
            roleId: 'role-admin',
            roleName: 'Admin',
        };
    }
}

/**
 * GET /v1/web/user/all - return all playground users.
 * Returns { users: [...] } to match the real API response shape.
 */
export function handleGetAllUsers(config, match) {
    return { users: getUsers() };
}

/**
 * GET /v1/web/user - return the current playground user object.
 */
export function handleGetCurrentUser(config, match) {
    return getPlaygroundUser();
}

/**
 * GET /v1/web/invitation - list all invitations.
 */
export function handleGetInvitations(config, match) {
    return { invitations: invitationStore.getAll() };
}

/**
 * GET /v1/web/invitation/:id - single invitation by id.
 */
export function handleGetInvitationById(config, match) {
    const id = match[1];
    const item = invitationStore.getById(id);
    if (!item) {
        throw new Error('Invitation with id ' + id + ' not found');
    }
    return item;
}

/**
 * POST /v1/web/invitation - create new invitation.
 */
export function handleCreateInvitation(config, match) {
    const data = typeof config.data === 'string' ? JSON.parse(config.data) : (config.data || {});
    const id = PlaygroundSessionStore.getNextId();
    const newInvitation = {
        ...data,
        id,
        status: 'PENDING',
        invitedBy: 'Playground Explorer',
        invitedDate: now(),
        lastModifiedDate: now(),
        lastModifiedBy: 'Playground Explorer',
    };
    return invitationStore.create(newInvitation);
}

/**
 * PUT /v1/web/invitation/:id - update existing invitation.
 */
export function handleUpdateInvitation(config, match) {
    const id = match[1];
    const data = typeof config.data === 'string' ? JSON.parse(config.data) : (config.data || {});
    const updated = {
        ...data,
        id,
        lastModifiedDate: now(),
        lastModifiedBy: 'Playground Explorer',
    };
    return invitationStore.update(id, updated);
}

/**
 * DELETE /v1/web/invitation/:id - remove invitation.
 */
export function handleDeleteInvitation(config, match) {
    const id = match[1];
    invitationStore.delete(id);
    return { success: true };
}
